import * as THREE from 'three';
import { bakeColored, createInstancedMesh, mergeBufferGeometries } from './batch';
import type { GlowSpot } from './glow';
import { mesh, roundedBox } from './helpers';
import type { ChannelMaterials, ParkMaterials } from './materials';
import {
  bulbTransforms,
  buntingRing,
  buntingSwag,
  createTwinkle,
  haloSpots,
  nightDressing,
  pennantGeometry,
  pennantRing,
  ringPositions,
  signMedallion,
  starGeometry,
  starTopper,
  visualTier,
  type VisualTier,
  type XYZ,
} from './tiers';

function bakedMesh(proto: THREE.Object3D, material: THREE.Material, cast = true): THREE.Mesh {
  const item = new THREE.Mesh(bakeColored(proto), material);
  item.castShadow = cast;
  item.receiveShadow = true;
  return item;
}

const CUP_COLORS = [0xe5514f, 0x4f9ddb, 0xf7d35c, 0x3fb8a8, 0xf0879e, 0xf08a3c];
const GONDOLA_COLORS = [0xe5514f, 0x4f9ddb, 0xf7d35c, 0x3fb8a8, 0xf0879e, 0x9a6fc9, 0xf08a3c, 0x7ed6a8];

/**
 * Ferris wheel envelope quoted by the COL-004A visual footprint recommendation (visual footprint =
 * `2 * (FERRIS_RADIUS + FERRIS_GONDOLA_HALF_WIDTH)` by the 0.7-deep base pad, times FERRIS_SCALE).
 * Keep these values in sync with that recommendation if they ever change.
 */
const FERRIS_RADIUS = 1.42;
const FERRIS_GONDOLA_HALF_WIDTH = 0.23;
/** Axle height above the ride base. */
const FERRIS_HUB_Y = 2.24;
/** Half-distance between the two parallel rims (wheel depth along Z). */
const FERRIS_RIM_Z = 0.16;
/** Z plane of each A-frame support; legs stay outside the rims and inside the 0.7 pad. */
const FERRIS_FRAME_Z = 0.29;
/** X spread of each A-frame foot from the axle line. */
const FERRIS_FOOT_X = 0.66;
const FERRIS_GONDOLA_COUNT = 8;
const FERRIS_SPIN_SPEED = 0.36;

/** Pirate-ship pivot height above the ride base; the boat hangs below it and swings around it. */
const PIRATE_PIVOT_Y = 2.3;
/** Keel depth below the pivot (pendulum length to the hull bottom). */
const PIRATE_KEEL_DROP = 1.75;
const PIRATE_SWING_AMPLITUDE = 0.72;
const PIRATE_SWING_SPEED = 1.3;

/**
 * Side profile of the boat in the XY plane (x = bow..stern, y = keel..deck), extruded across Z.
 * Bow and stern curve UP away from a flat keel so the hull reads as a boat, never as an inverted shell.
 */
function pirateHullGeometry(): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(-0.98, 0.66);
  shape.quadraticCurveTo(-1.02, 0.14, -0.55, 0);
  shape.lineTo(0.55, 0);
  shape.quadraticCurveTo(1.02, 0.1, 0.96, 0.56);
  shape.lineTo(0.96, 0.62);
  shape.quadraticCurveTo(0.72, 0.42, 0.5, 0.42);
  shape.lineTo(-0.62, 0.42);
  shape.quadraticCurveTo(-0.85, 0.44, -0.98, 0.66);
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: 0.62,
    bevelEnabled: true,
    bevelThickness: 0.05,
    bevelSize: 0.05,
    bevelSegments: 2,
    curveSegments: 6,
  });
  geometry.translate(0, 0, -0.31);
  return geometry;
}

/** Square sail with a billowed lower edge, hung from the yard and facing the swing plane. */
function pirateSailGeometry(): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(-0.38, 0.26);
  shape.lineTo(0.38, 0.26);
  shape.lineTo(0.38, -0.22);
  shape.quadraticCurveTo(0, -0.36, -0.38, -0.22);
  shape.closePath();
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: 0.04, bevelEnabled: false, curveSegments: 6 });
  geometry.translate(0, 0, -0.02);
  return geometry;
}

/**
 * Open teacup as two lathes: the outer wall + flat rim top, and a separate inner wall + floor
 * so the cavity can be baked in a darker tint. No cap disc anywhere above the rim.
 */
function teacupGeometry(): { outer: THREE.BufferGeometry; inner: THREE.BufferGeometry } {
  const outer = new THREE.LatheGeometry(
    [new THREE.Vector2(0, 0), new THREE.Vector2(0.15, 0), new THREE.Vector2(0.2, 0.1), new THREE.Vector2(0.245, 0.3), new THREE.Vector2(0.2, 0.3)],
    14,
  );
  const inner = new THREE.LatheGeometry(
    [new THREE.Vector2(0.2, 0.3), new THREE.Vector2(0.165, 0.1), new THREE.Vector2(0, 0.1)],
    14,
  );
  return { outer, inner };
}

/**
 * Radial spinning amusement ride: chunky deck, six toy teacups and a fixed candy canopy.
 * The deck spins as one baked group so the silhouette stays readable at park scale.
 */
export interface RideOptions {
  /** Upgrade level (1–3). Drives the COL-011B visual tier; footprint is unchanged. */
  level?: number;
}

/** Candy-stripe pavilion pillar (three stacked segments) used by the level 2+ teacups. */
function stripedPillar(mats: ParkMaterials, x: number, z: number, top: number): THREE.Mesh[] {
  const segment = top / 3;
  return [0, 1, 2].map((i) =>
    mesh(new THREE.CylinderGeometry(0.045, 0.045, segment, 7), i % 2 === 0 ? mats.red : mats.cream, {
      position: [x, segment * (i + 0.5), z],
    }),
  );
}

/** Giant teapot centrepiece: body, lid, spout and handle — the teacups' "park fantasy" anchor. */
function teapot(mats: ParkMaterials, channels: ChannelMaterials, y: number, scale: number): THREE.Group {
  const pot = new THREE.Group();
  pot.position.y = y;
  pot.scale.setScalar(scale);
  pot.add(mesh(new THREE.SphereGeometry(0.3, 14, 10), channels.primary, { scale: [1, 0.86, 1] }));
  pot.add(mesh(new THREE.CylinderGeometry(0.2, 0.24, 0.05, 12), mats.gold, { position: [0, 0.26, 0] }));
  pot.add(mesh(new THREE.SphereGeometry(0.16, 10, 8), channels.secondary, { position: [0, 0.3, 0], scale: [1, 0.5, 1] }));
  pot.add(mesh(new THREE.SphereGeometry(0.05, 8, 6), mats.gold, { position: [0, 0.4, 0] }));
  pot.add(mesh(new THREE.CylinderGeometry(0.035, 0.06, 0.34, 7), channels.primary, { position: [0.36, 0.1, 0], rotation: [0, 0, -0.9] }));
  pot.add(mesh(new THREE.TorusGeometry(0.14, 0.03, 6, 12, Math.PI), mats.gold, { position: [-0.3, 0.02, 0], rotation: [0, 0, Math.PI / 2] }));
  pot.add(mesh(new THREE.TorusGeometry(0.31, 0.02, 6, 20), mats.gold, { position: [0, -0.02, 0], rotation: [Math.PI / 2, 0, 0] }));
  return pot;
}

export function createSpinningRide(mats: ParkMaterials, channels: ChannelMaterials, options: RideOptions = {}): THREE.Group {
  const tier = visualTier(options.level);
  const root = new THREE.Group();

  const base = new THREE.Group();
  base.add(mesh(new THREE.CylinderGeometry(1.22, 1.28, 0.12, 20), channels.structure, { position: [0, 0.06, 0] }));
  base.add(mesh(new THREE.CylinderGeometry(1.08, 1.12, 0.1, 20), mats.stoneDark, { position: [0, 0.16, 0] }));
  // Canopy sits high and slightly smaller than the deck: from the fixed 37° camera the old low
  // canopy hid the rear cups. Base y 2.0 / radius 1.0 keeps all six cups visible under it.
  base.add(mesh(new THREE.CylinderGeometry(0.14, 0.18, 2.0, 10), channels.accent, { position: [0, 1.2, 0] }));
  base.add(mesh(new THREE.CylinderGeometry(0.22, 0.28, 0.12, 10), channels.accent, { position: [0, 0.28, 0] }));
  base.add(mesh(new THREE.SphereGeometry(0.1, 8, 6), channels.accent, { position: [0, 2.42, 0] }));
  const canopy = new THREE.ConeGeometry(1.0, 0.38, 10);
  canopy.translate(0, 2.19, 0);
  base.add(mesh(canopy, channels.primary));
  const canopyTrim = new THREE.TorusGeometry(0.98, 0.045, 7, 20);
  canopyTrim.rotateX(Math.PI / 2);
  canopyTrim.translate(0, 2.0, 0);
  base.add(mesh(canopyTrim, channels.accent));
  for (let i = 0; i < 8; i += 1) {
    const angle = (i / 8) * Math.PI * 2;
    base.add(
      mesh(new THREE.BoxGeometry(0.18, 0.08, 0.05), i % 2 === 0 ? channels.secondary : channels.primary, {
        position: [Math.cos(angle) * 0.95, 1.98, Math.sin(angle) * 0.95],
        rotation: [0.15, -angle, 0],
      }),
    );
  }

  const bulbs: XYZ[] = ringPositions(6, 0.98, 2.0);
  const halos = haloSpots(bulbs, 0.4);
  const pillarTop = 1.96;
  const pillarRadius = 1.16;
  if (tier >= 2) {
    // Level 2: candy-stripe pavilion pillars, teapot centrepiece, pennant crown and a fuller bulb rim.
    ringPositions(6, pillarRadius, 0, Math.PI / 6).forEach(([x, , z]) => {
      base.add(...stripedPillar(mats, x, z, pillarTop));
      base.add(mesh(new THREE.SphereGeometry(0.07, 8, 6), mats.gold, { position: [x, pillarTop + 0.04, z] }));
    });
    base.add(teapot(mats, channels, 1.12, tier >= 3 ? 1.2 : 0.95));
    base.add(pennantRing(mats, 8, 0.86, 2.08, 0.22, Math.PI / 8));
    // cream candy stripes laid on the canopy so the upgrade reads from above
    for (let i = 0; i < 10; i += 1) {
      const angle = (i / 10) * Math.PI * 2;
      base.add(
        mesh(new THREE.BoxGeometry(0.92, 0.03, 0.09), mats.cream, {
          position: [Math.cos(angle) * 0.5, 2.2, Math.sin(angle) * 0.5],
          rotation: [0, -angle, -Math.atan2(0.38, 1.0)],
          cast: false,
        }),
      );
    }
    bulbs.push(...ringPositions(6, 0.98, 2.0, Math.PI / 6), ...ringPositions(6, pillarRadius, pillarTop + 0.16, Math.PI / 6));
    halos.push(...haloSpots(ringPositions(6, pillarRadius, pillarTop + 0.16, Math.PI / 6), 0.5));
  }
  let twinkle: ((elapsed: number) => void) | null = null;
  if (tier >= 3) {
    // Level 3: second canopy tier with gold trim and star, steam puffs, bunting between pillars.
    const crown = new THREE.ConeGeometry(0.62, 0.34, 10);
    crown.translate(0, 2.62, 0);
    base.add(mesh(crown, channels.secondary));
    const crownTrim = new THREE.TorusGeometry(0.6, 0.04, 7, 20);
    crownTrim.rotateX(Math.PI / 2);
    crownTrim.translate(0, 2.46, 0);
    base.add(mesh(crownTrim, mats.gold));
    base.add(starTopper(mats, 0.22, 2.86));
    for (const [dx, dy, r] of [
      [0.5, 1.5, 0.05],
      [0.58, 1.62, 0.065],
      [0.64, 1.78, 0.08],
    ] as const) {
      base.add(mesh(new THREE.SphereGeometry(r, 8, 6), mats.cream, { position: [dx, dy, 0], cast: false }));
    }
    base.add(buntingRing(mats, 6, pillarRadius, pillarTop - 0.02, Math.PI / 6, 4));
  }
  root.add(bakedMesh(base, mats.baked));
  if (tier >= 3) {
    const crownBulbs = ringPositions(10, 0.62, 2.48);
    const crownBulbMesh = createInstancedMesh(new THREE.SphereGeometry(0.045, 7, 5), channels.lights, bulbTransforms(crownBulbs), {
      cast: false,
      receive: false,
    });
    root.add(crownBulbMesh);
    twinkle = createTwinkle(crownBulbMesh, crownBulbs);
    halos.push(...haloSpots(crownBulbs, 0.4), { position: [0, 3.0, 0], size: 0.9 });
  }
  if (tier >= 2) {
    root.add(...nightDressing(mats, halos, { radius: tier >= 3 ? 2.4 : 2.0, y: 0.23 }));
  }

  const spinner = new THREE.Group();
  const proto = new THREE.Group();
  proto.add(mesh(new THREE.CylinderGeometry(1.02, 1.05, 0.08, 20), channels.secondary, { position: [0, 0.24, 0] }));
  const rim = new THREE.TorusGeometry(1.02, 0.05, 7, 22);
  rim.rotateX(Math.PI / 2);
  rim.translate(0, 0.28, 0);
  proto.add(mesh(rim, channels.accent));
  const cup = teacupGeometry();
  const cavityTint = new THREE.Color();
  for (let i = 0; i < 6; i += 1) {
    const angle = (i / 6) * Math.PI * 2;
    const x = Math.cos(angle) * 0.68;
    const z = Math.sin(angle) * 0.68;
    const paint = new THREE.MeshStandardMaterial({ color: CUP_COLORS[i]!, roughness: 0.72 });
    // Darker cavity tint so the open interior reads as depth even at park zoom.
    const cavity = new THREE.MeshStandardMaterial({ color: cavityTint.setHex(CUP_COLORS[i]!).multiplyScalar(0.5).getHex(), roughness: 0.8 });
    proto.add(mesh(new THREE.CylinderGeometry(0.27, 0.27, 0.04, 12), channels.secondary, { position: [x, 0.3, z] }));
    // Open teacup: outer wall, darker inner well, cream rim ring. Nothing caps the opening.
    proto.add(mesh(cup.outer, paint, { position: [x, 0.32, z] }));
    proto.add(mesh(cup.inner, cavity, { position: [x, 0.32, z] }));
    const rimRing = new THREE.TorusGeometry(0.225, 0.028, 6, 16);
    rimRing.rotateX(Math.PI / 2);
    proto.add(mesh(rimRing, mats.cream, { position: [x, 0.62, z] }));
    // Chunky C handle on the outward side, ends buried in the cup wall.
    proto.add(mesh(new THREE.TorusGeometry(0.12, 0.038, 6, 12, Math.PI), mats.gold, {
      position: [x + Math.cos(angle) * 0.22, 0.49, z + Math.sin(angle) * 0.22],
      rotation: [0, -angle, -Math.PI / 2],
    }));
  }
  proto.add(mesh(new THREE.CylinderGeometry(0.18, 0.18, 0.06, 10), channels.accent, { position: [0, 0.3, 0] }));
  spinner.add(bakedMesh(proto, mats.baked));
  root.add(spinner);
  root.add(createInstancedMesh(new THREE.SphereGeometry(0.045, 7, 5), channels.lights, bulbTransforms(bulbs), { cast: false, receive: false }));

  root.userData.update = (elapsed: number) => {
    spinner.rotation.y = elapsed * 0.85;
    twinkle?.(elapsed);
  };
  root.userData.update(0);
  return root;
}

/**
 * Swinging pirate-ship attraction.
 *
 * Layout (ride-local): the boat's long axis runs along X. Two cream A-frames stand on the
 * port/starboard sides (z = ±0.98) with their legs spread along X, so they brace the swing
 * plane like a real ride and the hull never swings into them. A dark axle runs across the
 * apexes along Z. `pivot` is a dedicated group sitting exactly on that axle; the hull hangs
 * `PIRATE_KEEL_DROP` below it on two red arms, and the whole pivot rotates around its local Z,
 * so the boat's centre of mass traces a true pendulum arc: down through the low centre and up
 * to both sides. Nothing is wiggled at the top.
 */
/** Stylised palm: leaning trunk with a fan of leaf ellipsoids (level 3 pirate cove). */
function palmTree(mats: ParkMaterials, x: number, z: number, lean: number): THREE.Group {
  const palm = new THREE.Group();
  palm.position.set(x, 0.12, z);
  palm.rotation.z = lean;
  palm.add(mesh(new THREE.CylinderGeometry(0.035, 0.06, 0.9, 6), mats.trunk, { position: [0, 0.45, 0] }));
  for (let i = 0; i < 5; i += 1) {
    const angle = (i / 5) * Math.PI * 2;
    const leaf = mesh(new THREE.SphereGeometry(0.16, 7, 5), i % 2 === 0 ? mats.foliage : mats.foliageLight, {
      position: [Math.cos(angle) * 0.16, 0.92, Math.sin(angle) * 0.16],
      rotation: [0, -angle, 0.35],
      scale: [1.6, 0.35, 0.6],
    });
    palm.add(leaf);
  }
  palm.add(mesh(new THREE.SphereGeometry(0.05, 6, 5), mats.woodDark, { position: [0, 0.88, 0] }));
  return palm;
}

export function createPirateShip(mats: ParkMaterials, options: RideOptions = {}): THREE.Group {
  const tier = visualTier(options.level);
  const root = new THREE.Group();

  const frames = new THREE.Group();
  frames.add(mesh(roundedBox(1.35, 0.12, 1.55, 0.05), mats.stoneDark, { position: [0, 0.06, 0] }));
  const legLean = Math.atan(0.78 / PIRATE_PIVOT_Y);
  const legLength = Math.hypot(0.78, PIRATE_PIVOT_Y) + 0.1;
  for (const side of [-1, 1]) {
    const z = side * 0.98;
    for (const foot of [-1, 1]) {
      // Feet at x = ±0.78 on the ground, apex at x = 0 on the axle: a real "A".
      frames.add(mesh(new THREE.BoxGeometry(0.34, 0.14, 0.3), mats.stoneDark, { position: [foot * 0.78, 0.07, z] }));
      // Teal steel legs: separate from the cream paths, the wooden hull and the blue Ferris wheel.
      frames.add(
        mesh(new THREE.BoxGeometry(0.17, legLength, 0.15), mats.teal, {
          position: [foot * 0.39, PIRATE_PIVOT_Y / 2, z],
          rotation: [0, 0, foot * legLean],
        }),
      );
    }
    frames.add(mesh(new THREE.BoxGeometry(0.98, 0.1, 0.1), mats.cream, { position: [0, 0.95, z] }));
    frames.add(mesh(new THREE.CylinderGeometry(0.15, 0.15, 0.24, 10), mats.gold, { position: [0, PIRATE_PIVOT_Y, z], rotation: [Math.PI / 2, 0, 0] }));
  }
  frames.add(mesh(new THREE.CylinderGeometry(0.06, 0.06, 2.0, 8), mats.iron, { position: [0, PIRATE_PIVOT_Y, 0], rotation: [Math.PI / 2, 0, 0] }));

  const lanternSpots: XYZ[] = [];
  const halos: GlowSpot[] = [];
  if (tier >= 2) {
    // Level 2: corner lantern posts, gold apex caps, a splash pool under the hull and a treasure chest.
    for (const foot of [-1, 1]) {
      for (const side of [-1, 1]) {
        const x = foot * 0.78;
        const z = side * 0.98;
        frames.add(mesh(new THREE.CylinderGeometry(0.025, 0.035, 0.62, 6), mats.iron, { position: [x, 0.45, z] }));
        frames.add(mesh(new THREE.ConeGeometry(0.09, 0.07, 4), mats.iron, { position: [x, 0.85, z], rotation: [0, Math.PI / 4, 0] }));
        lanternSpots.push([x, 0.76, z]);
        halos.push({ position: [x, 0.76, z], size: 0.55 });
      }
      frames.add(mesh(new THREE.SphereGeometry(0.1, 8, 6), mats.gold, { position: [0, PIRATE_PIVOT_Y + 0.2, foot * 0.98] }));
    }
    frames.add(mesh(new THREE.CylinderGeometry(0.62, 0.62, 0.03, 20), mats.water, { position: [0, 0.135, 0], scale: [1.5, 1, 0.9], cast: false }));
    frames.add(mesh(roundedBox(0.22, 0.14, 0.15, 0.02), mats.woodDark, { position: [0.55, 0.19, -0.62] }));
    frames.add(mesh(roundedBox(0.22, 0.05, 0.15, 0.02), mats.gold, { position: [0.55, 0.28, -0.62] }));
    for (const side of [-1, 1]) {
      frames.add(mesh(new THREE.BoxGeometry(0.98, 0.04, 0.04), mats.gold, { position: [0, 1.02, side * 0.98] }));
    }
  }
  if (tier >= 3) {
    // Level 3: bulb strings up every leg, palm cove, pennant bunting between the apexes and a flag crown.
    for (const side of [-1, 1]) {
      const z = side * 0.98;
      for (const foot of [-1, 1]) {
        for (let i = 1; i <= 5; i += 1) {
          const t = i / 6;
          lanternSpots.push([foot * 0.78 * (1 - t), 0.14 + (PIRATE_PIVOT_Y - 0.14) * t, z + side * 0.1]);
        }
      }
      halos.push({ position: [0, PIRATE_PIVOT_Y + 0.2, z], size: 0.7 });
    }
    frames.add(palmTree(mats, -0.62, 0.66, 0.18));
    frames.add(palmTree(mats, 0.62, 0.7, -0.14));
    frames.add(
      buntingSwag(
        mats,
        new THREE.Vector3(0, PIRATE_PIVOT_Y + 0.26, -0.98),
        new THREE.Vector3(0, PIRATE_PIVOT_Y + 0.26, 0.98),
        6,
        0.16,
      ),
    );
    frames.add(pennantRing(mats, 4, 0.05, PIRATE_PIVOT_Y + 0.26, 0.16).translateZ(-0.98));
    frames.add(pennantRing(mats, 4, 0.05, PIRATE_PIVOT_Y + 0.26, 0.16).translateZ(0.98));
  }
  root.add(bakedMesh(frames, mats.baked));

  let twinkle: ((elapsed: number) => void) | null = null;
  if (lanternSpots.length > 0) {
    const lanterns = createInstancedMesh(new THREE.BoxGeometry(0.1, 0.12, 0.1), mats.lamp, bulbTransforms(lanternSpots), {
      cast: false,
      receive: false,
    });
    root.add(lanterns);
    if (tier >= 3) {
      twinkle = createTwinkle(lanterns, lanternSpots);
    }
    root.add(...nightDressing(mats, halos, { radius: tier >= 3 ? 2.2 : 1.8, y: 0.16 }));
  }

  // Dedicated pivot group on the suspension axis; everything that swings lives under it.
  const pivot = new THREE.Group();
  pivot.position.set(0, PIRATE_PIVOT_Y, 0);
  const swing = new THREE.Group();
  for (const side of [-1, 1]) {
    const z = side * 0.44;
    swing.add(mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.16, 8), mats.gold, { position: [0, 0, z], rotation: [Math.PI / 2, 0, 0] }));
    // Cream arms: distinct from the red sail, the teal legs and the wooden hull in every swing phase.
    swing.add(mesh(new THREE.BoxGeometry(0.13, PIRATE_KEEL_DROP - 0.25, 0.1), mats.cream, { position: [0, -(PIRATE_KEEL_DROP - 0.25) / 2, z] }));
    swing.add(mesh(roundedBox(0.22, 0.14, 0.1, 0.03), mats.gold, { position: [0, -PIRATE_KEEL_DROP + 0.3, z * 0.95] }));
  }

  // Hull positioned below the pivot BEFORE any rotation is applied.
  const boat = new THREE.Group();
  boat.position.set(0, -PIRATE_KEEL_DROP, 0);
  boat.add(mesh(pirateHullGeometry(), mats.woodDark));
  boat.add(mesh(new THREE.BoxGeometry(1.1, 0.04, 0.58), mats.woodLight, { position: [-0.06, 0.44, 0] }));
  boat.add(mesh(new THREE.BoxGeometry(1.14, 0.06, 0.06), mats.red, { position: [-0.06, 0.47, 0.3] }));
  boat.add(mesh(new THREE.BoxGeometry(1.14, 0.06, 0.06), mats.red, { position: [-0.06, 0.47, -0.3] }));
  for (const x of [-0.44, -0.14, 0.16]) {
    boat.add(mesh(new THREE.BoxGeometry(0.1, 0.16, 0.5), mats.cream, { position: [x, 0.52, 0] }));
  }
  boat.add(mesh(roundedBox(0.4, 0.3, 0.62, 0.04), mats.wood, { position: [0.74, 0.66, 0] }));
  boat.add(mesh(new THREE.BoxGeometry(0.44, 0.06, 0.66), mats.red, { position: [0.74, 0.84, 0] }));
  boat.add(mesh(new THREE.SphereGeometry(0.08, 8, 6), mats.gold, { position: [-1.0, 0.7, 0] }));
  boat.add(mesh(new THREE.CylinderGeometry(0.025, 0.035, 0.42, 6), mats.wood, { position: [-1.14, 0.78, 0], rotation: [0, 0, 1.2] }));
  boat.add(mesh(new THREE.CylinderGeometry(0.04, 0.05, 1.05, 7), mats.wood, { position: [-0.1, 0.94, 0] }));
  boat.add(mesh(new THREE.BoxGeometry(0.82, 0.05, 0.05), mats.wood, { position: [-0.1, 1.3, 0] }));
  boat.add(mesh(pirateSailGeometry(), mats.red, { position: [-0.1, 1.02, 0] }));
  boat.add(mesh(new THREE.BoxGeometry(0.78, 0.09, 0.05), mats.cream, { position: [-0.1, 1.02, 0] }));
  boat.add(mesh(new THREE.BoxGeometry(0.26, 0.13, 0.03), mats.charcoal, { position: [0.03, 1.5, 0] }));
  boat.add(mesh(new THREE.SphereGeometry(0.05, 7, 5), mats.gold, { position: [-0.1, 1.5, 0] }));
  if (tier >= 2) {
    // Level 2 hull: gold gunwale rails, a second red pennant and a cream stripe on the sail.
    for (const side of [-1, 1]) {
      boat.add(mesh(new THREE.BoxGeometry(1.16, 0.03, 0.03), mats.gold, { position: [-0.06, 0.51, side * 0.31] }));
    }
    boat.add(mesh(new THREE.BoxGeometry(0.22, 0.1, 0.03), mats.red, { position: [0.02, 1.64, 0] }));
    boat.add(mesh(new THREE.BoxGeometry(0.78, 0.05, 0.05), mats.gold, { position: [-0.1, 1.16, 0] }));
  }
  if (tier >= 3) {
    // Level 3 hull: crow's nest, stern lantern post and a striped bow sail.
    boat.add(mesh(new THREE.CylinderGeometry(0.11, 0.08, 0.1, 8), mats.woodDark, { position: [-0.1, 1.42, 0] }));
    boat.add(mesh(new THREE.CylinderGeometry(0.02, 0.03, 0.34, 6), mats.wood, { position: [0.78, 0.98, 0], rotation: [0, 0, -0.5] }));
    boat.add(mesh(new THREE.BoxGeometry(0.09, 0.11, 0.09), mats.lamp, { position: [0.86, 1.12, 0], cast: false }));
    boat.add(mesh(new THREE.CylinderGeometry(0.03, 0.04, 0.7, 6), mats.wood, { position: [0.5, 0.9, 0] }));
    boat.add(mesh(pirateSailGeometry(), mats.cream, { position: [0.5, 0.95, 0], scale: [0.6, 0.6, 1] }));
    boat.add(mesh(new THREE.BoxGeometry(0.46, 0.06, 0.05), mats.red, { position: [0.5, 0.95, 0] }));
  }
  swing.add(boat);
  pivot.add(bakedMesh(swing, mats.baked));
  root.add(pivot);

  root.userData.update = (elapsed: number) => {
    // Pendulum: rotate the pivot around the axle axis, never translate the hull.
    pivot.rotation.z = Math.sin(elapsed * PIRATE_SWING_SPEED) * PIRATE_SWING_AMPLITUDE;
    twinkle?.(elapsed);
  };
  root.userData.update(0);
  return root;
}

/** Static frame of the Ferris wheel: pad, boarding deck, two A-frames and the axle. */
function ferrisSupports(mats: ParkMaterials, channels: ChannelMaterials, tier: VisualTier): THREE.Group {
  const supports = new THREE.Group();
  const padTop = 0.12;
  supports.add(mesh(roundedBox(1.35, padTop, 0.7, 0.05), mats.stoneDark, { position: [0, padTop / 2, 0] }));

  // Boarding deck under the lowest gondola, open toward the +Z queue side.
  const deckTop = padTop + 0.1;
  supports.add(mesh(roundedBox(0.84, 0.1, 0.5, 0.03), mats.curb, { position: [0, padTop + 0.05, 0.03] }));
  for (const side of [-1, 1]) {
    for (const z of [-0.17, 0.23]) {
      supports.add(mesh(new THREE.BoxGeometry(0.04, 0.26, 0.04), mats.cream, { position: [side * 0.38, deckTop + 0.13, z] }));
    }
    supports.add(mesh(new THREE.BoxGeometry(0.04, 0.035, 0.44), mats.cream, { position: [side * 0.38, deckTop + 0.25, 0.03] }));
    // Entrance gate posts at the front of the deck.
    supports.add(mesh(new THREE.BoxGeometry(0.05, 0.3, 0.05), mats.cream, { position: [side * 0.17, deckTop + 0.15, 0.3] }));
  }
  supports.add(mesh(roundedBox(0.44, 0.06, 0.06, 0.02), channels.accent, { position: [0, deckTop + 0.32, 0.3] }));

  // Two A-frames, one on each side of the wheel, legs spread along X so the "A" faces the camera.
  const legRise = FERRIS_HUB_Y - padTop;
  const legLean = Math.atan(FERRIS_FOOT_X / legRise);
  const legLength = Math.hypot(FERRIS_FOOT_X, legRise) + 0.08;
  const legXAt = (y: number) => (FERRIS_FOOT_X * (FERRIS_HUB_Y - y)) / legRise;
  for (const side of [-1, 1]) {
    const z = side * FERRIS_FRAME_Z;
    for (const foot of [-1, 1]) {
      supports.add(mesh(roundedBox(0.3, 0.1, 0.12, 0.03), mats.stoneDark, { position: [foot * FERRIS_FOOT_X, padTop + 0.05, z] }));
      supports.add(
        mesh(new THREE.BoxGeometry(0.14, legLength, 0.11), channels.structure, {
          position: [(foot * FERRIS_FOOT_X) / 2, (padTop + FERRIS_HUB_Y) / 2, z],
          rotation: [0, 0, foot * legLean],
        }),
      );
    }
    for (const tieY of [0.62, 1.18]) {
      supports.add(mesh(new THREE.BoxGeometry(legXAt(tieY) * 2 + 0.1, 0.08, 0.08), mats.cream, { position: [0, tieY, z] }));
    }
    // Apex head with a gold bearing disc on both faces.
    supports.add(mesh(roundedBox(0.32, 0.28, 0.1, 0.04), channels.structure, { position: [0, FERRIS_HUB_Y, z] }));
    supports.add(mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.12, 14), channels.accent, { position: [0, FERRIS_HUB_Y, z], rotation: [Math.PI / 2, 0, 0] }));
  }
  // Ground rails tie the two frames together under the wheel's outer edge.
  for (const side of [-1, 1]) {
    supports.add(mesh(new THREE.BoxGeometry(0.1, 0.08, FERRIS_FRAME_Z * 2), channels.structure, { position: [side * FERRIS_FOOT_X, padTop + 0.12, 0] }));
  }
  supports.add(mesh(new THREE.CylinderGeometry(0.055, 0.055, FERRIS_FRAME_Z * 2, 8), mats.iron, { position: [0, FERRIS_HUB_Y, 0], rotation: [Math.PI / 2, 0, 0] }));

  if (tier >= 2) {
    // Level 2: corner planters with blooms, apex pennants and a round entrance medallion over the gate.
    const bloomTints = [mats.pink, mats.yellow, mats.red, mats.purple];
    [
      [-0.6, -0.28],
      [0.6, -0.28],
      [-0.6, 0.28],
      [0.6, 0.28],
    ].forEach(([x, z], i) => {
      supports.add(mesh(new THREE.CylinderGeometry(0.1, 0.12, 0.1, 10), mats.curb, { position: [x!, padTop + 0.05, z!] }));
      supports.add(mesh(new THREE.SphereGeometry(0.1, 8, 6), mats.foliageDark, { position: [x!, padTop + 0.13, z!], scale: [1, 0.6, 1] }));
      supports.add(mesh(new THREE.SphereGeometry(0.045, 6, 5), bloomTints[i % bloomTints.length]!, { position: [x!, padTop + 0.2, z!] }));
    });
    for (const side of [-1, 1]) {
      const z = side * FERRIS_FRAME_Z;
      supports.add(mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.3, 4), mats.iron, { position: [0, FERRIS_HUB_Y + 0.28, z] }));
      supports.add(mesh(pennantGeometry(0.2, 0.12), side < 0 ? mats.red : mats.yellow, { position: [0, FERRIS_HUB_Y + 0.43, z], cast: false }));
    }
    const medallion = signMedallion(mats, 0.15, channels.primary);
    medallion.position.set(0, deckTop + 0.55, 0.3);
    supports.add(medallion);
  }
  if (tier >= 3) {
    // Level 3: bunting from both apexes down to the pad corners and a gold crown ring on each apex head.
    for (const side of [-1, 1]) {
      const z = side * FERRIS_FRAME_Z;
      for (const foot of [-1, 1]) {
        supports.add(
          buntingSwag(
            mats,
            new THREE.Vector3(0, FERRIS_HUB_Y + 0.22, z + side * 0.08),
            new THREE.Vector3(foot * FERRIS_FOOT_X, padTop + 0.16, z + side * 0.12),
            6,
            0.16,
          ),
        );
      }
      const crownRing = new THREE.TorusGeometry(0.2, 0.025, 6, 16);
      supports.add(mesh(crownRing, mats.gold, { position: [0, FERRIS_HUB_Y, z + side * 0.06] }));
    }
  }
  return supports;
}

/** Rotating wheel structure: two rims, cream spokes, rim ties, gondola pivots and the hub drum. */
function ferrisWheelStructure(
  mats: ParkMaterials,
  channels: ChannelMaterials,
  tier: VisualTier,
): { structure: THREE.Group; twinkle: ((elapsed: number) => void) | null } {
  const proto = new THREE.Group();
  // Rims are flattened tori: a chunky band seen from the front, thin along Z so the
  // gondolas hang between them and the A-frames stay clear.
  const rimGeometry = new THREE.TorusGeometry(FERRIS_RADIUS, 0.09, 8, 48);
  rimGeometry.scale(1, 1, 0.5);
  const innerRingGeometry = new THREE.TorusGeometry(0.6, 0.045, 7, 26);
  for (const z of [-FERRIS_RIM_Z, FERRIS_RIM_Z]) {
    proto.add(mesh(rimGeometry, channels.primary, { position: [0, 0, z] }));
    proto.add(mesh(innerRingGeometry, channels.accent, { position: [0, 0, z] }));
    const spokes: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 8; i += 1) {
      const spoke = new THREE.BoxGeometry(0.05, FERRIS_RADIUS * 2 - 0.1, 0.04);
      spoke.rotateZ((i / 8) * Math.PI);
      spokes.push(spoke);
    }
    proto.add(mesh(mergeBufferGeometries(spokes), channels.secondary, { position: [0, 0, z] }));
  }
  for (let i = 0; i < FERRIS_GONDOLA_COUNT * 2; i += 1) {
    const angle = (i / (FERRIS_GONDOLA_COUNT * 2)) * Math.PI * 2;
    const position: [number, number, number] = [Math.cos(angle) * FERRIS_RADIUS, Math.sin(angle) * FERRIS_RADIUS, 0];
    if (i % 2 === 0) {
      // Gondola pivot bars span both rims.
      proto.add(mesh(new THREE.CylinderGeometry(0.05, 0.05, FERRIS_RIM_Z * 2 + 0.1, 8), channels.accent, { position, rotation: [Math.PI / 2, 0, 0] }));
    } else {
      proto.add(mesh(new THREE.BoxGeometry(0.05, 0.05, FERRIS_RIM_Z * 2), channels.secondary, { position, rotation: [0, 0, angle] }));
    }
  }
  proto.add(mesh(new THREE.CylinderGeometry(0.26, 0.26, 0.4, 16), channels.primary, { rotation: [Math.PI / 2, 0, 0] }));
  for (const side of [-1, 1]) {
    proto.add(mesh(new THREE.CylinderGeometry(0.17, 0.17, 0.04, 14), channels.accent, { position: [0, 0, side * 0.205], rotation: [Math.PI / 2, 0, 0] }));
    proto.add(mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.02, 10), channels.secondary, { position: [0, 0, side * 0.222], rotation: [Math.PI / 2, 0, 0] }));
  }
  if (tier >= 2) {
    // Level 2: thin gold ring hugging the outer rim on both faces (premium trim, visible from above).
    const goldRim = new THREE.TorusGeometry(FERRIS_RADIUS + 0.17, 0.035, 6, 48);
    for (const z of [-FERRIS_RIM_Z, FERRIS_RIM_Z]) {
      proto.add(mesh(goldRim, mats.gold, { position: [0, 0, z] }));
    }
  }
  if (tier >= 3) {
    // Level 3: gold star medallion on the front hub face and a second gold ring mid-spoke.
    proto.add(mesh(starGeometry(0.2, 0.09, 0.03), mats.gold, { position: [0, 0, 0.245] }));
    proto.add(mesh(new THREE.TorusGeometry(1.05, 0.03, 6, 40), mats.gold, { position: [0, 0, FERRIS_RIM_Z + 0.03] }));
  }
  const structure = new THREE.Group();
  structure.add(bakedMesh(proto, mats.baked));

  // Marquee bulbs on the outer edge between the rims: they never cross the A-frame planes.
  // Level 2 doubles the ring and adds spoke bulbs on the front face; level 3 adds an inner ring
  // and emissive spoke rays so the wheel becomes a starburst at night.
  const rimCount = tier >= 2 ? FERRIS_GONDOLA_COUNT * 4 : FERRIS_GONDOLA_COUNT * 2;
  const bulbs: XYZ[] = Array.from({ length: rimCount }, (_, i) => {
    const angle = ((i + 0.5) / rimCount) * Math.PI * 2;
    return [Math.cos(angle) * (FERRIS_RADIUS + 0.12), Math.sin(angle) * (FERRIS_RADIUS + 0.12), 0] as XYZ;
  });
  const frontZ = FERRIS_RIM_Z + 0.07;
  if (tier >= 2) {
    for (let i = 0; i < 8; i += 1) {
      const angle = (i / 8) * Math.PI * 2;
      bulbs.push([Math.cos(angle) * 0.72, Math.sin(angle) * 0.72, frontZ]);
    }
  }
  if (tier >= 3) {
    for (let i = 0; i < 16; i += 1) {
      const angle = ((i + 0.5) / 16) * Math.PI * 2;
      bulbs.push([Math.cos(angle) * 1.05, Math.sin(angle) * 1.05, frontZ]);
    }
    const rays = Array.from({ length: 8 }, (_, i) => {
      const angle = (i / 8) * Math.PI * 2;
      return {
        position: [Math.cos(angle) * 0.82, Math.sin(angle) * 0.82, frontZ - 0.02] as XYZ,
        rotation: [0, 0, angle] as XYZ,
        scale: [0.9, 0.03, 0.03] as XYZ,
      };
    });
    structure.add(createInstancedMesh(new THREE.BoxGeometry(1, 1, 1), channels.lights, rays, { cast: false, receive: false }));
  }
  const bulbMesh = createInstancedMesh(new THREE.SphereGeometry(0.05, 7, 5), channels.lights, bulbTransforms(bulbs), {
    cast: false,
    receive: false,
  });
  structure.add(bulbMesh);
  return { structure, twinkle: tier >= 3 ? createTwinkle(bulbMesh, bulbs) : null };
}

/**
 * Gondola prototypes. The shell (roof + body) is baked white so the per-instance colour tints it
 * and the cabin colour reads from the top-down camera; the trim (window strip, skirt, hanger
 * arms, pivot sleeve) keeps fixed colours and is a second instanced mesh.
 */
function ferrisGondolaGeometries(mats: ParkMaterials): { shell: THREE.BufferGeometry; trim: THREE.BufferGeometry } {
  const shellProto = new THREE.Group();
  const white = new THREE.MeshStandardMaterial({ color: 0xffffff });
  shellProto.add(mesh(roundedBox(FERRIS_GONDOLA_HALF_WIDTH * 2, 0.07, 0.2, 0.025), white, { position: [0, -0.13, 0] }));
  shellProto.add(mesh(roundedBox(0.42, 0.3, 0.18, 0.06), white, { position: [0, -0.33, 0] }));

  const trimProto = new THREE.Group();
  trimProto.add(mesh(new THREE.BoxGeometry(0.43, 0.05, 0.19), mats.charcoal, { position: [0, -0.19, 0] }));
  trimProto.add(mesh(roundedBox(0.34, 0.05, 0.15, 0.02), mats.cream, { position: [0, -0.49, 0] }));
  const armAngle = Math.atan2(0.17, 0.13);
  for (const side of [-1, 1]) {
    trimProto.add(mesh(new THREE.BoxGeometry(0.035, 0.24, 0.035), mats.cream, { position: [side * 0.085, -0.065, 0], rotation: [0, 0, side * armAngle] }));
  }
  trimProto.add(mesh(new THREE.CylinderGeometry(0.065, 0.065, 0.12, 8), mats.gold, { rotation: [Math.PI / 2, 0, 0] }));
  return { shell: bakeColored(shellProto), trim: bakeColored(trimProto) };
}

/**
 * Ferris wheel landmark: double-rim blue wheel with cream spokes on two warm A-frames,
 * eight upright two-tone gondolas that hang between the rims, marquee bulbs on the outer
 * edge and a small boarding deck with a gate facing the queue side (+Z).
 */
export function createFerrisWheel(mats: ParkMaterials, channels: ChannelMaterials, options: RideOptions = {}): THREE.Group {
  const tier = visualTier(options.level);
  const root = new THREE.Group();
  root.add(bakedMesh(ferrisSupports(mats, channels, tier), mats.baked));

  const wheel = new THREE.Group();
  wheel.position.set(0, FERRIS_HUB_Y, 0);
  const { structure, twinkle } = ferrisWheelStructure(mats, channels, tier);
  wheel.add(structure);

  if (tier >= 2) {
    // Static halo ring around the rim (the rim bulbs are evenly spaced so the halo needn't spin),
    // apex glows and a warm pool on the pad. Cool-tinted halos keep the wheel's icy sparkle.
    const halos: GlowSpot[] = Array.from({ length: 16 }, (_, i) => {
      const angle = (i / 16) * Math.PI * 2;
      return { position: [Math.cos(angle) * (FERRIS_RADIUS + 0.12), FERRIS_HUB_Y + Math.sin(angle) * (FERRIS_RADIUS + 0.12), 0], size: 0.5 };
    });
    if (tier >= 3) {
      halos.push({ position: [0, FERRIS_HUB_Y, 0.3], size: 1.4 });
      for (const side of [-1, 1]) {
        halos.push({ position: [0, FERRIS_HUB_Y + 0.3, side * FERRIS_FRAME_Z], size: 0.6 });
      }
    }
    root.add(...nightDressing(mats, halos, { radius: tier >= 3 ? 2.0 : 1.6, y: 0.135 }, true));
  }

  const gondolaGeometry = ferrisGondolaGeometries(mats);
  const gondolaShells = createInstancedMesh(
    gondolaGeometry.shell,
    mats.baked,
    GONDOLA_COLORS.map((color) => ({ position: [0, 0, 0], color })),
  );
  const gondolaTrims = createInstancedMesh(
    gondolaGeometry.trim,
    mats.baked,
    GONDOLA_COLORS.map(() => ({ position: [0, 0, 0] })),
  );
  wheel.add(gondolaShells, gondolaTrims);
  root.add(wheel);

  const dummy = new THREE.Object3D();
  root.userData.update = (elapsed: number) => {
    const spin = elapsed * FERRIS_SPIN_SPEED;
    wheel.rotation.z = spin;
    twinkle?.(elapsed);
    for (let i = 0; i < FERRIS_GONDOLA_COUNT; i += 1) {
      const angle = (i / FERRIS_GONDOLA_COUNT) * Math.PI * 2;
      dummy.position.set(Math.cos(angle) * FERRIS_RADIUS, Math.sin(angle) * FERRIS_RADIUS, 0);
      // Gondolas stay upright (counter-rotate) with a gentle pendulum lag for life.
      dummy.rotation.set(0, 0, -spin + Math.sin(elapsed * 1.5 + i * 0.9) * 0.05);
      dummy.scale.setScalar(1);
      dummy.updateMatrix();
      gondolaShells.setMatrixAt(i, dummy.matrix);
      gondolaTrims.setMatrixAt(i, dummy.matrix);
    }
    gondolaShells.instanceMatrix.needsUpdate = true;
    gondolaTrims.instanceMatrix.needsUpdate = true;
  };
  root.userData.update(0);
  return root;
}
